import { Injectable } from '@angular/core';
import { ApiHttpClient } from 'app/http/clients/api';
import { Observable } from 'rxjs';
import { Call, CallUpdates } from 'app/models/dto/call.model';
import { InitiateCall } from 'app/store/call/call.actions';
import { Store } from '@ngxs/store';
import { DirectoryListItem } from 'app/models/dto/directory-list-item.model';
import { Borrower } from 'app/models/dto/borrower.model';

@Injectable({
  providedIn: 'root'
})
export class SoftphoneService {

  constructor(
    private http: ApiHttpClient,
    private store: Store
  ) { }

  postCall(call: Call): Observable<any> {
    return this.http.post<Call>('/phone/start-call', call);
  }

  // Redundant.  Remove once legacy softphone is taken out of pipeline.
  directDialNumber(toNumber: number | string): Observable<any> {
    return this.http.post<Call>('/phone/start-call', { toNumber, directDial: true });
  }

  initiateSecondCall(toNumber: string, currentCallId: number): Observable<any> {
    return this.http.post<Call>('/phone/start-call', { currentCallId, toNumber, secondCall: true });
  }

  getCurrentCallState(): Observable<any> {
    return this.http.get<Call>('/phone/get-call-state', {});
  }

  mergeCalls(activeCallId: number, secondaryCallId: number): Observable<any> {
    return this.http.post<Call>('/phone/merge-calls', { activeCallId, secondaryCallId });
  }

  transferCall(twilioNumber: string, callId: number): Observable<any> {
    return this.http.post<Call>('/phone/transfer-call', { callId, twilioNumber });
  }

  swapCalls(fromCall: number, toCall: number): Observable<any> {
    return this.http.post<Call>('/phone/swap-calls', { fromCall, toCall });
  }

  updateCall(callId: number, updates: CallUpdates): Observable<any> {
    return this.http.put<CallUpdates>(`/call/${callId}`, updates);
  }

  cancelCall(callId: number): Observable<any> {
    return this.http.put<Call>(`/phone/cancel-call/${callId}`, { callId });
  }

  disconnectCall(callId: number): Observable<any> {
    return this.http.post<Call>('/phone/disconnect-call', { callId });
  }

  getCall(callId: number): Observable<any> {
    return this.http.get<number>(`/call/${callId}`);
  }

  answerCall(callId: number): Observable<any> {
    return this.http.post<Call>('/phone/answer-call', { callId });
  }

  answerQueueCall(callId: number): Observable<any> {
    return this.http.post<Call>('/phone/answer-queue-call', { callId });
  }

  dismissQueueCall(callId: number, updates: CallUpdates): Observable<any> {
    return this.http.post<Call>('/phone/dismiss-queue-call', { callId, updates });
  }

  dismissCall(callId: number, updates: CallUpdates): Observable<any> {
    return this.http.post<Call>('/phone/dismiss-call', { callId, updates });
  }

  addToExistingBusiness(callId: number, borrowerId: number): Observable<any> {
    return this.http.put<any>(`/call/${callId}`, { updatedBorrowerId: borrowerId });
  }

  flagAsWrongNumber(borrowerId: number, number: string | undefined): Observable<any> {
    return this.http.put<any>(`/wrong-number/${borrowerId}`, { 'toNumber': number });
  }

  callBorrower(borrowerId: number, phone: string | number, borrower: Borrower): Observable<any> {
    return this.store.dispatch(new InitiateCall({ borrowerId, phone, borrower }));
  }

  placeCallOnHold(callId: number): Observable<any> {
    return this.http.post<Call>('/phone/place-call-on-hold', { callId });
  }

  takeCallOffHold(callId: number): Observable<any> {
    return this.http.post<Call>('/phone/take-call-off-hold', { callId });
  }

  getLendioDirectory(): Observable<DirectoryListItem[]> {
    return this.http.get<DirectoryListItem[]>('/phone/lender-user-directory');
  }

  silentMonitorCall(callId: number): Observable<any> {
    return this.http.post<Call>('/phone/silent-monitor', { callId });
  }

  toggleSilentMonitorMuteState(callId: number, muteState: boolean): Observable<any> {
    return this.http.post<Call>('/phone/toggle-silent-monitor-mute-state', { callId, muteState });
  }
}
